import type { WidgetContainerId, SelfOwnedWidget, WidgetId, WindowId } from '../selfOwnedWidget';
import type { WidgetContainerJSON } from './WidgetContainer';
import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Layout, Widget, WidgetTab } from '@thinkalpha/ui-types/contracts/workspace';
import { widgetToContractModel } from 'src/lib/widget';
import { isSelfOwnedWidget } from 'src/services/WorkspaceService/selfOwnedWidgetRegistry';
import type { RootState, RootInstance as Store } from 'src/store';
import type { ContainerLayoutViewModel, ContainerViewModel, WidgetTabViewModel } from 'src/store/types';

export const WidgetContainerModel = createModel(
    (init: { id: WidgetContainerId; windowId: WindowId; layout: Layout; store: Store }) => {
        const position = signal<Layout>(init.layout);
        const tabs = signal<readonly WidgetTab[]>([]);
        const activeTabId = signal<string | null>(null);
        const isMaximized = signal<boolean>(false);
        const selfOwnedWidgets = signal<ReadonlyMap<WidgetId, SelfOwnedWidget<unknown>>>(new Map());

        // `effect` lets createModel tear down the Redux subscription on dispose.
        effect(() => {
            return init.store
                .subscribeSelector(
                    (state: RootState) => state.layout.byContainerId[init.id] as ContainerLayoutViewModel | undefined,
                    { leading: true },
                )
                .handle((vm) => {
                    if (!vm) return;
                    position.value = {
                        x: vm.x,
                        y: vm.y,
                        w: vm.w,
                        h: vm.h,
                        ...(vm.screen !== undefined ? { screen: vm.screen } : {}),
                    };
                });
        });

        effect(() => {
            return init.store
                .subscribeSelector(
                    (state: RootState) => state.container.byId[init.id] as ContainerViewModel | undefined,
                    { leading: true },
                )
                .handle((vm) => {
                    if (!vm) {
                        activeTabId.value = null;
                        tabs.value = [];
                        return;
                    }
                    activeTabId.value = vm.activeTabId ?? null;
                });
        });

        // Tabs are normalized separately from containers in Redux.
        effect(() => {
            return init.store
                .subscribeSelector(
                    (state: RootState) => {
                        const cvm = state.container.byId[init.id] as ContainerViewModel | undefined;
                        if (!cvm) return undefined;
                        const result: WidgetTabViewModel[] = [];
                        for (const tabId of cvm.tabIds) {
                            const tabVm = state.tab.byId[tabId] as WidgetTabViewModel | undefined;
                            if (tabVm) result.push(tabVm);
                        }
                        return result;
                    },
                    { leading: true },
                )
                .handle((tabVms) => {
                    if (!tabVms) {
                        tabs.value = [];
                        return;
                    }
                    tabs.value = tabVms.map((tabVm): WidgetTab => {
                        const widget: Widget = isSelfOwnedWidget(tabVm.widget)
                            ? (tabVm.widget as Widget)
                            : widgetToContractModel(tabVm.widget);
                        return { id: tabVm.id, name: tabVm.name, widget };
                    });
                });
        });

        effect(() => {
            return init.store
                .subscribeSelector((state: RootState) => state.container.maximizedContainerId === init.id, {
                    leading: true,
                })
                .handle((m) => {
                    isMaximized.value = m;
                });
        });

        const dirty: ReadonlySignal<boolean> = computed(() => {
            for (const widget of selfOwnedWidgets.value.values()) {
                if (widget.dirty.value) return true;
            }
            return false;
        });

        return {
            position,
            tabs,
            activeTabId,
            isMaximized,
            selfOwnedWidgets,
            dirty,
            setSelfOwnedWidgets(next: ReadonlyMap<WidgetId, SelfOwnedWidget<unknown>>): void {
                selfOwnedWidgets.value = next;
            },
            async toJSON(): Promise<WidgetContainerJSON> {
                return {
                    id: init.id,
                    tabs: [...tabs.value],
                    layout: { ...position.value },
                    activeTabId: activeTabId.value,
                };
            },
            async markCleanAll(): Promise<void> {
                for (const widget of selfOwnedWidgets.value.values()) {
                    widget.markClean();
                }
            },
        };
    },
);

export type WidgetContainerModel = InstanceType<typeof WidgetContainerModel>;
